import { Link } from "react-router-dom";
import { ArrowRight, Flame, TrendingDown, Leaf, Award, CheckCircle, Calculator, Gift } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { SEO } from "../components/SEO";

export const IntelligentHeating = () => {
  return (
    <div className="min-h-screen bg-navy-900 pt-20">
      <SEO 
        title="Intelligent Heating | Magic C Plumbing | Plumber & Heating Engineer in London"
        description="Upgrade to intelligent heating with high-efficiency heat pumps, condensing gas boilers, and luxurious underfloor heating. Enjoy up to 70% savings, major state subsidies, and superior comfort. Get your personalized quote."
        canonical="/intelligent-heating"
      />

      {/* Hero */}
      <section className="py-24 bg-gradient-to-b from-navy-900 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-block mb-6">
              <span className="bg-sage-500/20 text-sage-300 px-6 py-3 rounded-full text-base font-medium backdrop-blur-md border border-sage-500/30 shadow-lg">
                Save up to 70% on Energy Bills
              </span>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight">
              Intelligent Heating Systems
            </h1>
            
            <p className="text-2xl md:text-3xl text-gray-200 leading-relaxed mb-12 max-w-4xl mx-auto">
              Experience ultimate comfort with cutting-edge heat pumps, underfloor heating, and smart controls. Lower bills, government grants available, superior luxury.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-sage-600 hover:bg-sage-700 text-white text-xl px-12 py-8 h-auto shadow-2xl">
                  Request a Personalized Quote
                  <ArrowRight className="ml-3 h-6 w-6" />
                </Button>
              </Link>
              <a href="tel:03337890987">
                <Button size="lg" variant="outline" className="border-2 border-white/80 text-white hover:bg-white hover:text-navy-900 text-xl px-12 py-8 h-auto backdrop-blur-md">
                  Call Expert Team
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-24 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              { icon: TrendingDown, stat: "Up to 70%", label: "Energy Savings", color: "sage" },
              { icon: Gift, stat: "£7,500+", label: "Government Grants", color: "yellow" },
              { icon: Leaf, stat: "Net Zero", label: "Carbon Neutral", color: "green" },
              { icon: Award, stat: "25 Years", label: "System Lifespan", color: "sage" }
            ].map((benefit) => {
              const Icon = benefit.icon;
              return (
                <Card key={benefit.label} className="text-center bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all">
                  <CardContent className="p-8">
                    <div className="bg-gradient-to-br from-sage-500 to-sage-600 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl">
                      <Icon className="h-10 w-10 text-white" />
                    </div>
                    <div className="text-4xl font-bold text-sage-400 mb-2">{benefit.stat}</div>
                    <p className="text-gray-300 font-medium">{benefit.label}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Heating Solutions */}
      <section className="py-24 bg-navy-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Our Intelligent Heating Solutions
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Choose from cutting-edge systems designed for maximum efficiency and comfort
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 max-w-7xl mx-auto">
            {[
              {
                title: "Air Source Heat Pumps",
                description: "Extract heat from outside air even in winter. 300-400% efficiency. Eligible for £7,500 Boiler Upgrade Scheme grant.",
                features: ["COP 3.0-4.0 efficiency", "£7,500 government grant", "70% running cost reduction", "25+ year lifespan"],
                image: "https://images.unsplash.com/photo-1667491295131-c6ccecd35f65?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHx1bmRlcmZsb29yJTIwaGVhdGluZyUyMHBpcGVzJTIwaW5zdGFsbGF0aW9uJTIwZmxvb3J8ZW58MHx8fHwxNzc2MDE5NDIzfDA&ixlib=rb-4.1.0&q=85"
              },
              {
                title: "Underfloor Heating",
                description: "Luxurious even warmth from floor to ceiling. Perfect heat distribution with 15-20% energy savings vs radiators.",
                features: ["Even heat distribution", "15-20% more efficient", "Invisible luxury", "Works with all flooring"],
                image: "https://images.unsplash.com/photo-1667491295131-c6ccecd35f65?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHx1bmRlcmZsb29yJTIwaGVhdGluZyUyMHBpcGVzJTIwaW5zdGFsbGF0aW9uJTIwZmxvb3J8ZW58MHx8fHwxNzc2MDE5NDIzfDA&ixlib=rb-4.1.0&q=85"
              },
              {
                title: "Smart Heating Controls",
                description: "AI-powered thermostats learn your schedule. Remote control, room-by-room zoning, weather compensation.",
                features: ["AI learning algorithms", "Remote smartphone control", "Room-by-room zones", "8-12% extra savings"],
                image: "https://images.unsplash.com/photo-1545259742-b4fd8fea67e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxzbWFydCUyMHRoZXJtb3N0YXQlMjBhcHAlMjBtb2Rlcm4lMjBoZWF0aW5nJTIwY29udHJvbHxlbnwwfHx8fDE3NzYwMTk0MjN8MA&ixlib=rb-4.1.0&q=85"
              },
              {
                title: "Hybrid Systems",
                description: "Combine heat pump with gas boiler for maximum efficiency and reliability in all conditions.",
                features: ["Best of both technologies", "Optimal fuel selection", "Maximum reliability", "Future-proof investment"],
                image: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=600&q=80"
              },
              {
                title: "High-Efficiency Boilers",
                description: "Latest condensing boilers with 92-95% efficiency. ErP A-rated. 10-year warranties available.",
                features: ["92-95% efficiency", "Compact design", "Ultra-quiet operation", "10-year warranty"],
                image: "https://images.unsplash.com/photo-1575299737366-39c143459bc5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHx3YWxsJTIwbW91bnRlZCUyMGNvbmRlbnNpbmclMjBib2lsZXIlMjBtb2Rlcm4lMjBpbnN0YWxsYXRpb258ZW58MHx8fHwxNzc2MDE5NDIyfDA&ixlib=rb-4.1.0&q=85"
              },
              {
                title: "Solar Thermal Integration",
                description: "Combine with solar panels for free hot water. 50-70% hot water from sun. ROI in 7-10 years.",
                features: ["50-70% free hot water", "£400+ annual savings", "20+ year lifespan", "Green energy credits"],
                image: "https://images.unsplash.com/photo-1668097613572-40b7c11c8727?w=600&q=80"
              }
            ].map((solution) => (
              <Card key={solution.title} className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all group overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={solution.image} 
                    alt={solution.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-900/90 to-transparent"></div>
                </div>
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-4">{solution.title}</h3>
                  <p className="text-gray-300 mb-6 leading-relaxed">{solution.description}</p>
                  <ul className="space-y-3">
                    {solution.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-sage-400 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-200 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Financial Benefits */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-navy-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Financial & Environmental Benefits
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="bg-gradient-to-br from-sage-600 to-sage-700 text-white border-0 shadow-2xl">
                <CardContent className="p-10">
                  <Calculator className="h-16 w-16 mb-6" />
                  <h3 className="text-3xl font-bold mb-4">Typical Annual Savings</h3>
                  <div className="space-y-4 text-lg">
                    <div className="flex justify-between border-b border-white/20 pb-2">
                      <span>Heat Pump vs Gas:</span>
                      <span className="font-bold">£800-1,200/year</span>
                    </div>
                    <div className="flex justify-between border-b border-white/20 pb-2">
                      <span>Underfloor Heating:</span>
                      <span className="font-bold">+15% efficiency</span>
                    </div>
                    <div className="flex justify-between border-b border-white/20 pb-2">
                      <span>Smart Controls:</span>
                      <span className="font-bold">+8-12% savings</span>
                    </div>
                    <div className="flex justify-between pt-4 text-xl font-bold">
                      <span>Total Potential:</span>
                      <span>£1,000-1,500/year</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardContent className="p-10">
                  <Gift className="h-16 w-16 text-sage-400 mb-6" />
                  <h3 className="text-3xl font-bold text-white mb-4">Available Grants 2026</h3>
                  <ul className="space-y-4 text-gray-200">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-sage-400 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-bold text-lg">Boiler Upgrade Scheme: £7,500</p>
                        <p className="text-sm text-gray-400">For air source heat pumps</p>
                      </div>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-sage-400 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-bold text-lg">Energy Company Obligation (ECO4)</p>
                        <p className="text-sm text-gray-400">Additional support for eligible homes</p>
                      </div>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-sage-400 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-bold text-lg">Council Tax Rebates</p>
                        <p className="text-sm text-gray-400">Local authority schemes</p>
                      </div>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <div className="text-center">
              <Link to="/contact">
                <Button size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-navy-900 font-bold text-xl px-12 py-8 shadow-2xl">
                  <Gift className="mr-3 h-6 w-6" />
                  Claim Your State Subsidies
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-navy-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Upgrade to Intelligent Heating?
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10">
            Get your personalized quote with detailed savings calculations and available grants
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-sage-600 hover:bg-sage-700 text-white text-xl px-12 py-8">
                Upgrade to Intelligent Heating
                <ArrowRight className="ml-3 h-6 w-6" />
              </Button>
            </Link>
            <a href="tel:03337890987">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-navy-900 text-xl px-12 py-8">
                Call Heating Specialists
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
